import React, { useMemo, useState } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Card,
  Alert,
  Spinner,
  Collapse,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useGrantBrandMembershipMutation,
  useListBrandUsersQuery,
  useListMasterAccountBrandsQuery,
  useListMasterAccountsQuery,
  useRevokeBrandMembershipMutation,
  useUpdateUserBrandAccessMutation,
  type BrandUserRow,
} from "@/features/accounts/accountsApi";
import { useListBrandsQuery } from "@/features/brands/brandsApi";

type BrandAccessLevel = "franchisee" | "corporate";

function toStorageScope(value: string): BrandAccessLevel {
  return value === "corporate" ? "corporate" : "franchisee";
}

function userLabel(user: BrandUserRow): string {
  return (
    user.displayName?.trim() ||
    user.email?.trim() ||
    user.auth0Sub ||
    "Unknown user"
  );
}

type BrandUsersPanelProps = {
  masterAccountId: string;
  brandId: string;
  brandName: string;
  open: boolean;
  onError: (message: string) => void;
};

const BrandUsersPanel: React.FC<BrandUsersPanelProps> = ({
  masterAccountId,
  brandId,
  brandName,
  open,
  onError,
}) => {
  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useListBrandUsersQuery(
    { masterAccountId, brandId },
    { skip: !open || !masterAccountId || !brandId },
  );
  const [updateAccess, { isLoading: updating }] =
    useUpdateUserBrandAccessMutation();
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);

  const handleScopeChange = async (
    user: BrandUserRow,
    nextScope: BrandAccessLevel,
  ) => {
    const current = toStorageScope(user.roleScope || "franchisee");
    if (current === nextScope) return;

    onError("");
    const key = `${brandId}:${user.auth0Sub}`;
    setUpdatingKey(key);
    try {
      await updateAccess({
        masterAccountId,
        auth0Sub: user.auth0Sub,
        brandId,
        roleScope: nextScope,
        reason: "updated from SA Client Brands page",
      }).unwrap();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      onError(
        e?.data?.message ||
          e?.message ||
          `Failed to update access for ${userLabel(user)} on "${brandName}"`,
      );
    } finally {
      setUpdatingKey(null);
    }
  };

  if (!open) return null;

  if (isLoading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" /> Loading brand users…
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mb-0">
        Failed to load users for this brand.{" "}
        <Button size="sm" variant="outline-danger" onClick={() => refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  const users = data?.users || [];

  if (users.length === 0) {
    return (
      <Alert variant="light" className="mb-0 border">
        <strong>Portfolio assigned — no per-user Brand access yet.</strong>
        <div className="text-muted small mt-1">
          This Client is enrolled in the Brand portfolio, but no users have
          individual Brand access records yet. Owners or other users may need
          seeding via API or self-join before Full or Limited can be set here.
        </div>
      </Alert>
    );
  }

  return (
    <Table size="sm" responsive className="mb-0">
      <thead>
        <tr>
          <th>User</th>
          <th>Client role</th>
          <th>Brand role</th>
          <th style={{ minWidth: 140 }}>Brand access</th>
          <th>Locations</th>
        </tr>
      </thead>
      <tbody>
        {users.map((user) => {
          const scope = toStorageScope(user.roleScope || "franchisee");
          const rowBusy = updating && updatingKey === `${brandId}:${user.auth0Sub}`;
          const locationsLabel =
            scope === "corporate"
              ? "All brand locations"
              : user.assignedLocationCount != null
                ? `${user.assignedLocationCount} assigned`
                : "—";
          return (
            <tr key={user.auth0Sub}>
              <td>
                <div>{userLabel(user)}</div>
                {user.email && user.displayName && (
                  <div className="text-muted small">{user.email}</div>
                )}
              </td>
              <td>{user.clientRole || "—"}</td>
              <td>{user.brandRole || "—"}</td>
              <td>
                <Form.Select
                  size="sm"
                  value={scope}
                  disabled={rowBusy}
                  aria-label={`Brand access for ${userLabel(user)} on ${brandName}`}
                  onChange={(e) =>
                    handleScopeChange(user, toStorageScope(e.target.value))
                  }
                >
                  <option value="franchisee">Limited</option>
                  <option value="corporate">Full</option>
                </Form.Select>
              </td>
              <td className="text-muted small">{locationsLabel}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const ClientBrands: React.FC = () => {
  const { companyNumber: rawId } = useParams<{ companyNumber: string }>();
  const masterAccountId = rawId ? decodeURIComponent(rawId) : "";
  const navigate = useNavigate();

  const { data: clientsData } = useListMasterAccountsQuery({ kind: "client" });
  const client = useMemo(
    () =>
      (clientsData?.masterAccounts || []).find(
        (c) => c.masterAccountId === masterAccountId,
      ),
    [clientsData, masterAccountId],
  );

  const {
    data: membershipData,
    isLoading,
    isError,
    refetch,
  } = useListMasterAccountBrandsQuery(masterAccountId, {
    skip: !masterAccountId,
  });
  const { data: allBrandsData } = useListBrandsQuery();
  const [grant, { isLoading: granting }] = useGrantBrandMembershipMutation();
  const [revoke, { isLoading: revoking }] = useRevokeBrandMembershipMutation();

  const [showAssign, setShowAssign] = useState(false);
  const [brandToAssign, setBrandToAssign] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedBrandIds, setExpandedBrandIds] = useState<Set<string>>(
    () => new Set(),
  );

  const assignedIds = new Set(
    (membershipData?.brands || []).map((b) => b.brandId),
  );
  const availableBrands = (allBrandsData?.brands || []).filter(
    (b) => b.status === "active" && !assignedIds.has(b.brandId),
  );

  const toggleBrandUsers = (brandId: string) => {
    setExpandedBrandIds((prev) => {
      const next = new Set(prev);
      if (next.has(brandId)) next.delete(brandId);
      else next.add(brandId);
      return next;
    });
  };

  const handleAssign = async () => {
    if (!brandToAssign) return;
    setActionError(null);
    try {
      await grant({
        masterAccountId,
        brandId: brandToAssign,
      }).unwrap();
      setShowAssign(false);
      setBrandToAssign("");
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setActionError(e?.data?.message || e?.message || "Assign failed");
    }
  };

  const handleRevoke = async (brandId: string, name: string) => {
    if (
      !window.confirm(
        `Revoke portfolio access to brand "${name}" for this client? This does not delete the brand.`,
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await revoke({
        masterAccountId,
        brandId,
        reason: "revoked from SA Client Brands page",
      }).unwrap();
      setExpandedBrandIds((prev) => {
        const next = new Set(prev);
        next.delete(brandId);
        return next;
      });
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setActionError(e?.data?.message || e?.message || "Revoke failed");
    }
  };

  const brands = membershipData?.brands || [];

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <PageTitle
              title={`Brands for ${client?.businessName || "this Client"}`}
            />
            <Button variant="outline-secondary" onClick={() => navigate("/accounts")}>
              Back to Clients
            </Button>
          </div>

          <p className="text-muted">
            Assign platform Brands to this Client&apos;s portfolio. Portfolio
            enrollment does not set Full or Limited for users — set Brand access
            per user after assignment. Full sees all brand locations and
            billing; Limited sees assigned locations only.
          </p>

          {actionError && (
            <Alert
              variant="warning"
              dismissible
              onClose={() => setActionError(null)}
            >
              {actionError}
            </Alert>
          )}

          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="outline-danger"
              onClick={() => {
                setActionError(null);
                setShowAssign(true);
              }}
            >
              Assign Brand
            </Button>
          </div>

          {isLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}
          {isError && (
            <Alert variant="danger">
              Failed to load brand memberships{" "}
              <Button size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          )}

          {!isLoading && !isError && (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Industry</th>
                  <th>Portfolio role</th>
                  <th>Enrolled</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => {
                  const usersOpen = expandedBrandIds.has(b.brandId);
                  return (
                    <React.Fragment key={b.brandId}>
                      <tr>
                        <td>{b.name}</td>
                        <td>{b.industry || "—"}</td>
                        <td>{b.accessLevel}</td>
                        <td>{b.grantedAt?.slice(0, 10) || "—"}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant={usersOpen ? "danger" : "outline-danger"}
                              disabled={revoking}
                              onClick={() => toggleBrandUsers(b.brandId)}
                              aria-expanded={usersOpen}
                            >
                              {usersOpen ? "Hide brand users" : "Brand users"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={revoking}
                              onClick={() => handleRevoke(b.brandId, b.name)}
                            >
                              Revoke portfolio
                            </Button>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={5} className="bg-light border-0 pt-0">
                          <Collapse in={usersOpen}>
                            <div className="p-3 border rounded">
                              <div className="fw-semibold mb-2">
                                Brand users — {b.name}
                              </div>
                              <p className="text-muted small mb-3">
                                Full and Limited are set independently for each
                                user (for example, one user may be Full while
                                another is Limited on the same Brand).
                              </p>
                              <BrandUsersPanel
                                masterAccountId={masterAccountId}
                                brandId={b.brandId}
                                brandName={b.name}
                                open={usersOpen}
                                onError={(message) =>
                                  setActionError(message || null)
                                }
                              />
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {brands.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No brands assigned yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAssign} onHide={() => setShowAssign(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Brand to Client portfolio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-0">
            <Form.Label>Brand</Form.Label>
            <Form.Select
              value={brandToAssign}
              onChange={(e) => setBrandToAssign(e.target.value)}
            >
              <option value="">Select a brand…</option>
              {availableBrands.map((b) => (
                <option key={b.brandId} value={b.brandId}>
                  {b.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Assigning enrolls this Client in the Brand portfolio only. Set Full
              or Limited for each user afterward under Brand users on the row
              below.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssign(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!brandToAssign || granting}
            onClick={handleAssign}
          >
            {granting ? "Assigning…" : "Assign to portfolio"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ClientBrands;
